import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import type { TrainingPlanIntake } from '@/lib/api/training-program';

interface TrainingPlanIntakeModalProps {
  isOpen: boolean;
  onClose: () => void;
  primaryFaultName: string;
  onSubmit: (intake: TrainingPlanIntake) => void | Promise<void>;
  isSubmitting?: boolean;
}

const practiceOptions: Array<{ value: TrainingPlanIntake['practice_frequency']; label: string }> = [
  { value: '1-2_per_week', label: '1–2x per week' },
  { value: '3-4_per_week', label: '3–4x per week' },
  { value: '5+_per_week', label: '5+ times per week' },
];

const sessionOptions: Array<{ value: TrainingPlanIntake['session_length']; label: string }> = [
  { value: '15-20_minutes', label: '15–20 minutes' },
  { value: '30_minutes', label: '30 minutes' },
  { value: '45+_minutes', label: '45+ minutes' },
];

const goalOptions: Array<{ value: NonNullable<TrainingPlanIntake['primary_goal']>; label: string }> = [
  { value: 'improve_ball_striking', label: 'Improve ball striking' },
  { value: 'be_more_consistent', label: 'Be more consistent' },
  { value: 'score_better', label: 'Score better' },
  { value: 'fix_specific_miss', label: 'Fix a specific miss' },
];

export function TrainingPlanIntakeModal({
  isOpen,
  onClose,
  primaryFaultName,
  onSubmit,
  isSubmitting = false,
}: TrainingPlanIntakeModalProps) {
  const [practiceFrequency, setPracticeFrequency] = useState<TrainingPlanIntake['practice_frequency']>('3-4_per_week');
  const [sessionLength, setSessionLength] = useState<TrainingPlanIntake['session_length']>('30_minutes');
  const [primaryGoal, setPrimaryGoal] = useState<TrainingPlanIntake['primary_goal'] | ''>('');

  useEffect(() => {
    if (isOpen) {
      setPracticeFrequency('3-4_per_week');
      setSessionLength('30_minutes');
      setPrimaryGoal('');
    }
  }, [isOpen]);

  const handleSubmit = () => {
    const intake: TrainingPlanIntake = {
      practice_frequency: practiceFrequency,
      session_length: sessionLength,
      primary_goal: primaryGoal || undefined,
    };
    onSubmit(intake);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto pb-safe">
        <DialogHeader>
          <DialogTitle className="text-[#0D382A]">Training Plan Intake</DialogTitle>
          <DialogDescription>
            Choose your practice preferences to personalize your plan.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <p className="text-sm text-muted-foreground">
              This training plan will be based on the following primary swing issue:
            </p>
            <p className="mt-2 text-base font-semibold text-foreground">{primaryFaultName}</p>
            <p className="mt-2 text-xs text-muted-foreground">
              You can update your plan later if your swing changes.
            </p>
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium text-foreground">Practice Frequency</Label>
            <RadioGroup
              value={practiceFrequency}
              onValueChange={(value) => setPracticeFrequency(value as TrainingPlanIntake['practice_frequency'])}
              className="space-y-2"
            >
              {practiceOptions.map((option) => (
                <Label key={option.value} htmlFor={`practice-${option.value}`} className="flex items-center gap-3">
                  <RadioGroupItem value={option.value} id={`practice-${option.value}`} />
                  <span className="text-sm text-foreground">{option.label}</span>
                </Label>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium text-foreground">Typical Session Length</Label>
            <RadioGroup
              value={sessionLength}
              onValueChange={(value) => setSessionLength(value as TrainingPlanIntake['session_length'])}
              className="space-y-2"
            >
              {sessionOptions.map((option) => (
                <Label key={option.value} htmlFor={`session-${option.value}`} className="flex items-center gap-3">
                  <RadioGroupItem value={option.value} id={`session-${option.value}`} />
                  <span className="text-sm text-foreground">{option.label}</span>
                </Label>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium text-foreground">Primary Goal (Optional)</Label>
            <RadioGroup
              value={primaryGoal}
              onValueChange={(value) => setPrimaryGoal(value as TrainingPlanIntake['primary_goal'])}
              className="space-y-2"
            >
              <Label htmlFor="goal-none" className="flex items-center gap-3">
                <RadioGroupItem value="" id="goal-none" />
                <span className="text-sm text-foreground">No specific goal</span>
              </Label>
              {goalOptions.map((option) => (
                <Label key={option.value} htmlFor={`goal-${option.value}`} className="flex items-center gap-3">
                  <RadioGroupItem value={option.value} id={`goal-${option.value}`} />
                  <span className="text-sm text-foreground">{option.label}</span>
                </Label>
              ))}
            </RadioGroup>
          </div>

          <div className="flex flex-col gap-2">
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Creating Plan...' : 'Create Training Plan'}
            </Button>
            <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface TrainingPlanReplaceConfirmModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onContinue: () => void;
}

export function TrainingPlanReplaceConfirmModal({
  isOpen,
  onCancel,
  onContinue,
}: TrainingPlanReplaceConfirmModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onCancel}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#0D382A]">Replace Training Plan?</DialogTitle>
          <DialogDescription>
            Confirm whether to replace your current plan.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            You already have an active training plan. Creating a new plan will replace your current one.
            Do you want to continue?
          </p>
          <div className="flex flex-col gap-2">
            <Button onClick={onContinue}>Continue</Button>
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
